/**
 * Utility functions to format and categorize PrefillCitations data for anonymous PUI emails
 *
 * Categories:
 * 1. Basic Demographics - Age, State, Gender, Tobacco Status
 * 2. Physical Health - Height, weight, vitals, physical conditions
 * 3. Household - Income, assets, household composition
 * 4. Medical Diagnoses - All diagnosis fields
 * 5. Medications & Treatments - Prescriptions, therapies
 * 6. Substance Use - Smoking, alcohol, drugs
 * 7. ADL/IADL - Activities of daily living and assistance needs
 * 8. Injuries & Surgeries - All injury-related fields
 * 9. Hospitalizations - ER visits, hospital stays
 * 10. Family Medical History - Parent health information
 * 11. Insurance - Health plans, Medicaid, legal docs
 */

import clientIntakeFormSchema from './clientIntakeFormSchema.json';

type SchemaField = {
  type: string;
  label: string;
  group: string;
};

const schema = clientIntakeFormSchema as Record<string, SchemaField>;

type CitationMap = Map<string, PrefillCitations>;

function buildCitationMap(citations: PrefillCitations[]): CitationMap {
  const map = new Map<string, PrefillCitations>();
  citations.forEach(citation => {
    map.set(citation.ref, citation);
  });
  return map;
}

function getLabel(ref: string): string | undefined {
  const field = schema[ref];
  return field?.label;
}

function formatValue(
  value: string | number | boolean | Date | undefined | null,
): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toISOString().split('T')[0];

  // Handle string values that might be arrays joined with |||
  if (typeof value === 'string' && value.includes('|||')) {
    const items = value.split('|||').filter(item => item.trim());
    return items.length > 0 ? items.join(', ') : undefined;
  }

  const stringValue = String(value).trim().toLowerCase();

  // Filter out "none", "no", or "none of these" answers
  if (stringValue === 'none' || stringValue === 'no' || stringValue === 'none of these') {
    return undefined;
  }

  return String(value);
}

function getCitationValue(map: CitationMap, ref: string): string | undefined {
  const citation = map.get(ref);
  if (!citation) return undefined;
  return formatValue(citation.answer);
}

function convertHeightToFeetAndInches(heightValue: number | string): {
  feet: number;
  inches: number;
} {
  let totalInches = 0;

  if (typeof heightValue === 'string') {
    const match = heightValue.match(/(\d+)'(\d+)"/);
    if (match) {
      const feet = parseInt(match[1], 10);
      const inches = parseInt(match[2], 10);
      totalInches = feet * 12 + inches;
    } else {
      totalInches = parseInt(heightValue, 10) || 0;
    }
  } else {
    totalInches = heightValue || 0;
  }

  if (totalInches <= 0) return { feet: 0, inches: 0 };
  return {
    feet: Math.floor(totalInches / 12),
    inches: totalInches % 12,
  };
}

/**
 * Demographics - Extracts core demographics from citations (using core_demographics refs)
 */
export function formatDemographics(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const demographicRefs = [
    'quotePrimaryAge',
    'quotePrimaryGender',
    'quoteUsaState',
    'quotePrimaryTobaccoStatus',
  ];

  const labelMap: Record<string, string> = {
    quotePrimaryAge: 'Age',
    quotePrimaryGender: 'Gender',
    quoteUsaState: 'State',
    quotePrimaryTobaccoStatus: 'Tobacco Status',
  };

  demographicRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = labelMap[ref];
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Basic Demographics',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Household Information
 */
export function formatHousehold(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const refs = [
    'clientMaritalStatus',
    'householdPartnered',
    'householdHouseholdSize',
    'clientLivingSiblingsCount',
    'householdLivingChildrenCount',
  ];

  refs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Household',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Physical Health Metrics
 */
export function formatPhysicalHealth(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const heightCitation = citationMap.get('clientHeightInches');
  if (heightCitation) {
    const heightInfo = convertHeightToFeetAndInches(heightCitation.answer as number | string);
    const label = getLabel('clientHeightInches');
    if (heightInfo.feet > 0 && label) {
      items.push({
        label,
        value: `${heightInfo.feet}'${heightInfo.inches}"`,
      });
    }
  }

  const weightCitation = citationMap.get('clientWeightPounds');
  const weightLabel = getLabel('clientWeightPounds');
  if (weightCitation && weightLabel) {
    items.push({ label: weightLabel, value: `${weightCitation.answer} lbs` });
  }

  const weightGainedCitation = citationMap.get('clientWeightGainedLastTwelveMonths');
  const weightGainedLabel = getLabel('clientWeightGainedLastTwelveMonths');
  if (weightGainedCitation && weightGainedLabel) {
    items.push({
      label: weightGainedLabel,
      value: `${weightGainedCitation.answer} lbs`,
    });
  }

  const weightLostCitation = citationMap.get('clientWeightLostLastTwelveMonths');
  const weightLostLabel = getLabel('clientWeightLostLastTwelveMonths');
  if (weightLostCitation && weightLostLabel) {
    items.push({
      label: weightLostLabel,
      value: `${weightLostCitation.answer} lbs`,
    });
  }

  const bpValue = getCitationValue(citationMap, 'clientBloodPressureReading');
  const bpLabel = getLabel('clientBloodPressureReading');
  if (bpValue && bpLabel) {
    items.push({ label: bpLabel, value: bpValue });
  }

  return {
    category: 'Physical Health Metrics',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Medical Diagnoses & Conditions
 */
export function formatMedicalDiagnoses(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const diagnosisRefs = [
    'clientDiagnosesList',
    'clientMentalHealthIssuesList',
    'clientHeartConditionDiagnosis',
    'clientHeartConditionDiagnosisYear',
    'clientHeartConditionDiagnosisTreatment',
    'clientHighBloodPressureYear',
    'clientHighBloodPressureControlled',
    'clientDiabetesTypeDiagnosisList',
    'clientDiabetesDiagnosisYear',
    'clientLastA1CReading',
    'clientDiabetesControlMethod',
    'clientMetabolicDisordersList',
    'clientMetabolicDisorderYear',
    'clientMetabolicDisorderControlled',
    'clientLungRespiratoryDiagnosis',
    'clientLungRespiratoryYear',
    'clientLungRespiratoryTreatment',
    'clientDigestiveDiagnosis',
    'clientDigestiveYear',
    'clientBrainInjuryDiagnosis',
    'clientBrainInjuryLastEpisodeYear',
    'clientBrainInjuryLimitations',
    'clientCognitiveMemoryDiagnosis',
    'clientCognitiveMemoryYear',
    'clientCognitiveMemorySeverity',
    'clientPsychiatricDiagnosis',
    'clientPsychiatricYear',
    'clientCancerTypeLocation',
    'clientCancerYear',
    'clientCancerStage',
    'clientCancerTreatment',
    'clientCancerTreatmentDate',
    'clientCancerRecurrenceSpread',
    'clientEENTDiagnosis',
    'clientEENTDYear',
    'clientEENTDSurgery',
    'clientEENTDSurgeryType',
    'clientEENTDLimits',
    'clientGenitourinaryDiagnosis',
    'clientGenitourinaryYear',
    'clientImmuneSystemDiagnosis',
    'clientImmuneSystemYear',
    'clientUndiagnosedCondition',
    'clientAdditionalMedicalHistory',
  ];

  diagnosisRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Medical Diagnoses',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Medications & Treatments
 */
export function formatMedicationsTreatments(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const medicationRefs = [
    'clientUsesRegularPrescriptionDrugs',
    'clientMedicationIntake',
    'clientMedChangeLastTwelveMonths',
    'clientMedChangeDescLastTwelveMonths',
    'clientMedChangeCauseLastTwelveMonths',
    'clientTherapyTypes',
    'clientPhysicalTherapyTense',
    'clientPhysicalTherapyFrequency',
    'clientPhysicalTherapyEnd',
    'clientPhysicalTherapyReason',
    'clientOccupationalTherapyTense',
    'clientOccupationalTherapyFrequency',
    'clientOccupationalTherapyEnd',
    'clientOccupationalTherapyReason',
    'clientChiropracticTherapyTense',
    'clientChiropracticTherapyFrequency',
    'clientChiropracticTherapyEnd',
    'clientChiropracticTherapyReason',
    'clientAssistiveDevicesList',
  ];

  medicationRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Medications & Treatments',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Substance Use History
 */
export function formatSubstanceUse(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const substanceRefs = [
    'clientSmokerEver',
    'clientSmokedRecently',
    'clientCigaretteLastFiveYears',
    'clientCigarPipeLastFiveYears',
    'clientVapeLastFiveYears',
    'clientChewTobaccoLastFiveYears',
    'clientAlcoholLastFiveYears',
    'clientMarijuanaLastFiveYears',
    'clientMarijuanaUsageType',
    'clientMedicalMarijuanaCondition',
    'clientMushroomLastFiveYears',
    'clientHardDrugsLastFiveYears',
    'clientSubstancesUsed',
  ];

  substanceRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Substance Use',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Activities of Daily Living (ADL) and Instrumental ADL
 */
export function formatADLIADL(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const adlRefs = [
    'clientActivitiesWithDifficultyList',
    'clientActivitiesReceivedAssistanceList',
    'clientAgeReceivedHelpBathing',
    'clientAgeReceivedHelpDressing',
    'clientAgeReceivedHelpEating',
    'clientAgeReceivedHelpToileting',
    'clientAgeReceivedHelpAccessingBed',
    'clientAgeReceivedHelpWalking',
    'clientAgeReceivedHelpClimbingStairs',
    'clientAgeReceivedHelpShopping',
    'clientAgeReceivedHelpTakingMedication',
    'clientAgeReceivedHelpPreparingMeals',
    'clientAgeReceivedHelpManagingFinances',
    'clientAgeReceivedHelpMakingPhoneCalls',
  ];

  adlRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'ADL/IADL',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Injuries & Surgeries
 */
export function formatInjuriesSurgeries(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const injuryRefs = [
    'clientInjuriesList',
    'clientInjuryArthritis',
    'clientInjuriesKnee',
    'clientInjuriesKneeYear',
    'clientInjuriesKneeSurgeryYear',
    'clientInjuriesKneeSurgeryPlanned',
    'clientInjuriesAnkle',
    'clientInjuriesAnkleYear',
    'clientInjuriesAnkleSurgeryYear',
    'clientInjuriesAnkleSurgeryPlanned',
    'clientInjuriesHip',
    'clientInjuriesHipYear',
    'clientInjuriesHipSurgeryYear',
    'clientInjuriesHipSurgeryPlanned',
    'clientInjuriesBack',
    'clientInjuriesBackYear',
    'clientInjuriesBackSurgeryYear',
    'clientInjuriesBackSurgeryPlanned',
    'clientInjuriesShoulder',
    'clientInjuriesShoulderYear',
    'clientInjuriesShoulderSurgeryYear',
    'clientInjuriesShoulderSurgeryPlanned',
    'clientInjuriesNeck',
    'clientInjuriesNeckYear',
    'clientInjuriesNeckSurgeryYear',
    'clientInjuriesNeckSurgeryPlanned',
    'clientInjuriesWrist',
    'clientInjuriesWristYear',
    'clientInjuriesWristSurgeryYear',
    'clientInjuriesWristSurgeryPlanned',
    'clientInjuriesElbow',
    'clientInjuriesElbowYear',
    'clientInjuriesElbowSurgeryYear',
    'clientInjuriesElbowSurgeryPlanned',
    'clientInjuriesFoot',
    'clientInjuriesFootYear',
    'clientInjuriesFootSurgeryYear',
    'clientInjuriesFootSurgeryPlanned',
    'clientInjuryJoints',
    'clientInjuryJointsYear',
    'clientInjuryJointsSurgeryYear',
    'clientInjuryJointsSurgeryPlanned',
    'clientInjuryJointInjectionLastTwelveMonths',
    'clientInjuryAdditionalDetails',
  ];

  injuryRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Injuries & Surgeries',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Hospitalizations & Emergency Care
 */
export function formatHospitalizations(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const hospitalizationRefs = [
    'clientPriorTwoYearHospitalizationCount',
    'clientHospitalizationReason',
    'clientPriorTwoYearERCount',
    'clientERReason',
    'clientHospitalizedERLastTwelveMonths',
    'clientLastDoctorVisitYear',
  ];

  hospitalizationRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Hospitalizations',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Family Medical History (Mother & Father)
 */
export function formatFamilyHistory(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const fatherRefs = [
    'clientFatherIsAlive',
    'clientFatherAgeAtSurveyDate',
    'clientFatherAgeOfDeathAtSurveyDate',
    'clientFatherRequiredAssistance',
    'clientFatherAssistanceDurationMonthCount',
    'clientFatherNursingHomeEver',
    'clientFatherNursingHomeMonthCount',
    'clientFatherCardiovascularEver',
    'clientFatherMemoryProblemsEver',
    'clientFatherMemoryDiagnosesList',
  ];

  const motherRefs = [
    'clientMotherIsAlive',
    'clientMotherAgeAtSurveyDate',
    'clientMotherAgeOfDeathAtSurveyDate',
    'clientMotherRequiredAssistance',
    'clientMotherAssistanceDurationMonthCount',
    'clientMotherNursingHomeEver',
    'clientMotherNursingHomeMonthCount',
    'clientMotherCardiovascularEver',
    'clientMotherMemoryProblemsEver',
    'clientMotherMemoryDiagnosesList',
  ];

  const hasFatherData = fatherRefs.some(ref => citationMap.has(ref));
  const hasMotherData = motherRefs.some(ref => citationMap.has(ref));

  if (hasFatherData) {
    items.push({ label: '', value: '=== Father ===' });
    fatherRefs.forEach(ref => {
      const value = getCitationValue(citationMap, ref);
      const label = getLabel(ref);
      if (value && label) {
        items.push({ label, value });
      }
    });
  }

  if (hasMotherData) {
    items.push({ label: '', value: '\n=== Mother ===' });
    motherRefs.forEach(ref => {
      const value = getCitationValue(citationMap, ref);
      const label = getLabel(ref);
      if (value && label) {
        items.push({ label, value });
      }
    });
  }

  return {
    category: 'Family Medical History',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Insurance, Legal Documents & Disability
 */
export function formatInsuranceLegal(citationMap: CitationMap): FormattedIntakeSection {
  const items: FormattedIntakeItem[] = [];

  const insuranceRefs = [
    'clientHealthPlanList',
    'clientOtherHealthPlanList',
    'clientDisabilityWorkersComp',
    'legalDocuments',
    'clientProfessionalSupportNetworkList',
  ];

  insuranceRefs.forEach(ref => {
    const value = getCitationValue(citationMap, ref);
    const label = getLabel(ref);
    if (value && label) {
      items.push({ label, value });
    }
  });

  return {
    category: 'Insurance & Legal',
    items,
    hasData: items.length > 0,
  };
}

/**
 * Main function to format entire intake survey into all categories (excludes PII)
 * Returns only sections with data
 */
export function formatCompleteIntakeSurvey(citationMap: CitationMap): FormattedIntakeSection[] {
  const allSections = [
    formatPhysicalHealth(citationMap),
    //formatHousehold(citationMap),
    formatMedicalDiagnoses(citationMap),
    formatMedicationsTreatments(citationMap),
    formatSubstanceUse(citationMap),
    formatADLIADL(citationMap),
    formatInjuriesSurgeries(citationMap),
    formatHospitalizations(citationMap),
    formatFamilyHistory(citationMap),
    //formatInsuranceLegal(citationMap),
  ];

  return allSections.filter(section => section.hasData);
}

/**
 * Format intake survey for anonymous PUI - returns sections array
 * Takes PrefillCitations array (including merged demographics) and uses schema labels
 */
export function formatIntakeForPUI(citations: PrefillCitations[]): FormattedIntakeSection[] {
  const citationMap = buildCitationMap(citations);
  return [formatDemographics(citationMap), ...formatCompleteIntakeSurvey(citationMap)];
}
